import {AsyncPipe} from '@angular/common';
import {Component, ElementRef, HostListener, OnInit, ViewChild} from '@angular/core';
import {MatDialog} from '@angular/material/dialog';
import {MatIcon} from '@angular/material/icon';
import {Asset} from '@app/asset';
import {InspectorService} from '@app/inspector/inspector.service';
import {Rect} from '@app/shared/euclid';
import {SiteConfig, SiteConfigService} from '@app/shared/site-config.service';
import {StringDialogComponent} from '@app/shared/string-dialog/string-dialog.component';
import {AppState, StateService} from '@app/state/state.service';
import {UploadDetailDialogComponent} from '@app/upload/upload-detail-dialog/upload-detail-dialog.component';
import {UploadData, UploadService} from '@app/upload/upload.service';

import {Observable, tap} from 'rxjs';

@Component({
    selector: 'frog-upload-drop',
    templateUrl: './upload-drop.component.html',
    styleUrls: ['./upload-drop.component.scss'],
    imports: [MatIcon, AsyncPipe],
})
export class UploadDropComponent implements OnInit {
    @ViewChild('root') root: ElementRef;
    public hidden: boolean;
    public itemCount: number;
    public state$: Observable<AppState>;
    public config$: Observable<SiteConfig>;
    public inspectedAsset$: Observable<Asset>;
    private canPaste: boolean;
    private streamId: string;
    private reviewId: string;

    constructor(
        private stateService: StateService,
        private uploadService: UploadService,
        private siteConfigService: SiteConfigService,
        private inspectorService: InspectorService,
        private matDialog: MatDialog,
    ) {
        this.hidden = true;
        this.canPaste = true;
    }

    ngOnInit(): void {
        this.state$ = this.stateService.state.pipe(
            tap((state) => {
                // This is kind of a hack.  When this component gets reused, the "hidden" variable gets flipped from a previous state
                // This was the only point i could find to ensure it was set correctly after reuse
                this.hidden = true;

                this.streamId = state.streamId;
                this.reviewId = state.reviewAsset;
            }),
        );
        this.config$ = this.siteConfigService.siteConfig;
        this.inspectedAsset$ = this.inspectorService.asset.pipe(
            tap((asset) => {
                this.canPaste = asset === null;
            }),
        );
    }

    drop(
        event: DragEvent,
        group: boolean,
        detail: boolean,
        assetId: string = null,
        parentAssetId: string = null,
    ) {
        this.hidden = true;
        if (event.dataTransfer.files.length) {
            event.preventDefault();

            const uploadData = this.uploadService.getUploadData(
                event.dataTransfer.items,
                this.streamId,
            );
            uploadData.asGroup = group;
            uploadData.parentAssetId = parentAssetId;

            if (uploadData.uploadItems.length === 1 && assetId !== null) {
                uploadData.uploadItems[0].assetId = assetId;
            }

            if (detail) {
                this.openUploadDetailDialog(uploadData);
            } else {
                this.uploadService.showUploadDialog();
                this.uploadService.upload(uploadData).subscribe();
            }
        }
    }

    @HostListener('window:dragenter', ['$event'])
    dragEnter(event: DragEvent) {
        const types = Array.from(event.dataTransfer.types);
        this.hidden = types.indexOf('Files') === -1;
        this.itemCount = event.dataTransfer.items.length;
        (event.target as HTMLElement).setAttribute('drop-active', 'true');
    }

    @HostListener('window:dragleave', ['$event'])
    dragLeave(event: DragEvent) {
        const rect = new Rect(this.root.nativeElement.getBoundingClientRect());
        const types = Array.from(event.dataTransfer.types);
        this.hidden =
            rect.containsPoint(event.clientX, event.clientY) || types.indexOf('Files') === -1;
        (event.target as HTMLElement).setAttribute('drop-active', 'false');
    }

    @HostListener('window:paste', ['$event'])
    pasteImage(event: ClipboardEvent) {
        if (!this.canPaste) {
            return;
        }

        const clipboardData = event.clipboardData;
        // Filter out only images for pasting support
        const matchType = /image.*/;
        const items = Array.from(clipboardData.items).filter((item) => item.type.match(matchType));

        const uploadData = this.uploadService.getUploadData(items, this.streamId);
        uploadData.parentAssetId = this.reviewId;
        if (!uploadData.isValid()) {
            return;
        }

        const reader = new FileReader();
        reader.readAsDataURL(uploadData.uploadItems[0].file);
        reader.onload = (event) => {
            const nameDialog = this.matDialog.open(StringDialogComponent, {
                data: {
                    title: 'Pasted Image',
                    message: 'Please enter a filename for the pasted image',
                    preview: reader.result
                },
            });
            nameDialog.afterClosed().subscribe((result) => {
                if (result) {
                    // Since this is a paste, you can only paste at most 1 thing, we can safely use index 0
                    uploadData.uploadItems[0].name = result;
                    this.uploadService.showUploadDialog();
                    this.uploadService.upload(uploadData).subscribe();
                }
            });
        };
    }

    cancelDrop(event: DragEvent) {
        event.stopPropagation();
        event.stopImmediatePropagation();
        event.preventDefault();
        this.hidden = true;
    }

    openUploadDetailDialog(uploadData: UploadData) {
        this.matDialog.open(UploadDetailDialogComponent, {
            data: uploadData,
            width: '80vw',
        });
    }
}
